import { useQuery } from "@apollo/client";
import { uniqBy } from "lodash";
import { ReactElement, useState } from "react";
import {
  GetOrganizationCareerTracksQuery,
  GetOrganizationCareerTracksQueryVariables,
} from "types/graphql-schema";

import getOrganizationCareerTracksQuery from "@apps/org-settings/graphql/get-organization-career-tracks-query";
import { currentOrganizationVar } from "@cache/cache";
import { ButtonSize } from "@components/button/button-constants";
import ComboboxGeneric, {
  ComboboxGenericButtonProps,
  ComboboxGenericOption,
} from "@components/combobox/generic-combobox";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull } from "@helpers/helpers";

export type CareerTrackPickerTrack = {
  id: number;
  title: string;
};

export const CareerTrackPickerDropdown = ({
  careerTracks,
  onChange,
  createLabel,
  onClickCreateNew,
  onlyTeamIds,
  children,
}: {
  careerTracks: CareerTrackPickerTrack[];
  onChange: (value: CareerTrackPickerTrack) => void;
  // Optional "Create new…" item shown at the top of the menu.
  createLabel?: string;
  onClickCreateNew?: () => void;
  // Lists only the tracks in these departments.
  onlyTeamIds?: readonly number[];
  children: ReactElement<ComboboxGenericButtonProps>;
}) => {
  const currentOrganization = currentOrganizationVar();
  const [options, setOptions] = useState<
    ComboboxGenericOption<CareerTrackPickerTrack>[]
  >([]);
  const careerTrackIds = careerTracks.map(({ id }) => id);
  const [canLoadData, setCanLoadData] = useState(false);

  const { loading } = useQuery<
    GetOrganizationCareerTracksQuery,
    GetOrganizationCareerTracksQueryVariables
  >(getOrganizationCareerTracksQuery, {
    variables: {
      organizationId: currentOrganization.id,
    },
    skip: !canLoadData,
    onCompleted: (data) => {
      const tracks = assertEdgesNonNull(data.careerTracks);
      setOptions(
        uniqBy(tracks, "id")
          .filter(
            (track) =>
              !onlyTeamIds ||
              (!!track.team && onlyTeamIds.includes(track.team.id)),
          )
          .map((track) => ({
            label: track.title,
            value: track,
          })),
      );
    },
  });

  const filteredOptions = options.filter(
    (option) => !option.value.id || !careerTrackIds.includes(option.value.id),
  );

  const handleChangeCareerTrack = (
    option: ComboboxGenericOption<CareerTrackPickerTrack>,
  ) => {
    onChange(option.value);
  };

  const handlePreloadData = () => {
    setCanLoadData(true);
  };

  return (
    <ComboboxGeneric
      aria-label="Career Track picker dropdown"
      loading={loading}
      value={null}
      options={filteredOptions}
      onChangeValue={handleChangeCareerTrack}
      onClickButton={handlePreloadData}
      createLabel={onClickCreateNew ? createLabel : undefined}
      onClickCreateNew={onClickCreateNew}
    >
      {children}
    </ComboboxGeneric>
  );
};

const CareerTrackPicker = ({
  careerTracks,
  onRemoveCareerTrack,
  onAddCareerTrack,
  canChange,
  onClickCreateNew,
  onlyTeamIds,
  size,
}: {
  careerTracks: CareerTrackPickerTrack[];
  onRemoveCareerTrack: (recipient: CareerTrackPickerTrack) => void;
  onAddCareerTrack: (option: CareerTrackPickerTrack) => void;
  canChange: boolean;
  onClickCreateNew?: () => void;
  onlyTeamIds?: readonly number[];
  size?: ButtonSize;
}) => {
  return (
    <div className="flex-1 flex gap-2 flex-wrap">
      {careerTracks.map((track) => (
        <div
          // Same height as the picker button beside it, on a grey fill so the
          // chips read as values rather than more buttons.
          className={classNames(
            "rounded-lg px-2 text-sm flex items-center gap-1 bg-gray-100",
            size === ButtonSize.small
              ? "h-[20px]"
              : size === ButtonSize.large
                ? "h-[32px]"
                : "h-[24px]",
          )}
          key={track.id}
        >
          {track.title}
          {canChange && (
            <button
              className="ml-0.5 rounded-full p-0.5 border bg-gray-50 text-gray-600 hover:text-white hover:bg-blue-600 hover:border-blue-600"
              onClick={() => onRemoveCareerTrack(track)}
            >
              <TopicflowIcon type="close" className="h-3 w-3" />
            </button>
          )}
        </div>
      ))}
      {canChange && (
        <CareerTrackPickerDropdown
          careerTracks={careerTracks}
          onChange={onAddCareerTrack}
          createLabel="Create new career track"
          onClickCreateNew={onClickCreateNew}
          onlyTeamIds={onlyTeamIds}
        >
          <ComboboxGeneric.Button size={size}>
            <TopicflowIcon type="plus" className="h-4 w-4" />
            Pick a Career Track
          </ComboboxGeneric.Button>
        </CareerTrackPickerDropdown>
      )}
    </div>
  );
};

export default CareerTrackPicker;
